import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, FormControl, Theme, Tooltip, Typography, Card } from '@material-ui/core';
import {
  Slider,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  CardContent,
  Drawer,
  Button,
  Box,
} from '@mui/material';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
  COLOR_PRIMARY,
  FILTER_FLOAT_BUTTON_Z_INDEX_DESKTOP,
  exchangeRates,
  COLOR_RANGE_AIRBNB_MARKERS,
  COLOR_TOOLTIP_BACKGROUND,
  FILTER_WIDTH,
  FILTER_FLOAT_BUTTON_Z_INDEX_MOBILE,
} from 'src/utils/const';
import { FilterPropTypes, FilterVal } from 'src/interfaces/iproptypes';
import {
  abbreviateNumber,
  convertCurrency,
  isMarketBeachfront,
  trackFilterActivation,
} from 'src/utils/functions';
import { COLOR_GREY_400, COLOR_PRIMARY_1, COLOR_PRIMARY_2 } from 'src/utils/colors';
import theme from 'src/core/MuiThemeProvider/theme';
import { updatePriceTiers } from 'src/store/Dashboard/actions';
import { PriceTiers } from 'src/interfaces/idashboard';
import TabBar from 'src/App/Dashboards/Market/Components/Tabs/TabBar';
import { useUpgradeMesageBasedOnFilterTab } from 'src/hooks/useUpgradeText';
import { useCannotAccessFilter } from 'src/hooks/useFeatureAccess';
import { AmenitiesIcons } from 'src/utils/icons';

import CloseIcon from '@mui/icons-material/Close';

const useStyles = makeStyles((theme: Theme) => ({
  filterContainer: {
    width: 'inherit',
    padding: '20px 30px',
    [theme.breakpoints.down(900)]: {
      width: '100%',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    marginTop: '170px',
    marginBottom: '75px',
  },
  tooltip: {
    backgroundColor: 'transparent',
    padding: 0,
    height: '80px',
  },
}));

const categoriesObject = {
  beachfront: 'Beachfront',
  amazing_views: 'Amazing Views',
};

const commonAmenitiesObject = {
  pool: 'Pool',
  hot_tub: 'Hot Tub',
  parking: 'Parking',
  pets: 'Pet Friendly',
  ac: 'AC',
};

const amenitiesObject = {
  heating: 'Heating',
  breakfast: 'Breakfast',
  dryer: 'Dryer',
  washer: 'Washer',
  gym: 'Gym',
  kitchen: 'Kitchen',
  tv: 'TV',
  wifi: 'WiFi',
  bbq_grill: 'BBQ Grill',
  fireplace: 'Fireplace',
};

const REVENUE_TIER_NAMES = ['Low revenue', 'Below avg', 'Above avg', 'Top earning'] as const;

const getRevenueTierBoundaries = (data: any): number[] => {
  if (!data) return [];
  if (Array.isArray(data)) return [...data].sort((a, b) => a - b);
  return Object.keys(data)
    .map(Number)
    .filter(n => !Number.isNaN(n))
    .sort((a, b) => a - b);
};

const getRevenueTierRangeLabel = (
  tierIndex: number,
  boundaries: number[],
  currency: string
): string => {
  const abbrev = (n: number) => abbreviateNumber(convertCurrency(currency, n)).toLowerCase();
  // 4 tiers: boundaries = [20th, 40th, 60th, 80th] %ile from cache (aligned with buildMarketBitmaps rev_bucket)
  if (boundaries.length < 3) return '—';
  if (tierIndex === 0) return `≤ ${abbrev(boundaries[0])}`;
  if (tierIndex === 1) return `${abbrev(boundaries[0])}–${abbrev(boundaries[1])}`;
  if (tierIndex === 2) return `${abbrev(boundaries[1])}–${abbrev(boundaries[2])}`;
  // Tier 3 (Top earning) = backend le100 = revenue > 60th %ile (breaks[2])
  return `> ${abbrev(boundaries[2])}`;
};

const getPriceTierRangeLabel = (
  tier: string,
  priceTiers: PriceTiers,
  currency: string
): string => {
  const tierData = priceTiers[tier as keyof PriceTiers];
  if (!tierData?.range || tierData.range.length < 2) return '—';
  const symbol = exchangeRates[currency]?.symbol ?? '$';
  const abbrev = (n: number) =>
    abbreviateNumber(convertCurrency(currency, n)).toLowerCase();
  const [min, max] = tierData.range;
  if (min === 0) return `≤ ${symbol}${abbrev(max)}`;
  if (max >= 999999) return `≥ ${symbol}${abbrev(min)}`;
  return `${symbol}${abbrev(min)}–${symbol}${abbrev(max)}`;
};

const FilterChip = ({
  label,
  value,
  variant,
  handleOnClick,
  styles,
  tooltipText,
}: {
  variant: 'filled' | 'outlined';
  label: string | number | JSX.Element;
  value: string | number | (number | 'all')[];
  handleOnClick: (value: string | number | (number | 'all')[]) => void;
  styles?: any;
  tooltipText?: string;
}) => {
  const classes = useStyles();

  const chipComponent = (
    <Chip
      label={label}
      variant={variant}
      onClick={() => handleOnClick(value)}
      sx={{
        minWidth: 50,
        marginTop: '10px',
        marginRight: '5px',
        borderColor: COLOR_PRIMARY_1,
        borderWidth: '1px',
        color: COLOR_PRIMARY_1,
        fontWeight: 500,
        '&.MuiChip-filled': {
          backgroundColor: COLOR_PRIMARY_1,
          color: '#fff',
        },
        ...styles,
      }}
    />
  );

  if (!tooltipText) {
    return chipComponent;
  }

  return (
    <Tooltip
      classes={{ tooltip: classes.tooltip }}
      title={
        <Card style={{ width: '160px', height: '60px' }}>
          <CardContent
            style={{ background: COLOR_TOOLTIP_BACKGROUND, height: '60px', fontSize: '18px' }}
          >
            <Grid container direction="column" alignItems="flex-start">
              <Typography variant="body1">{tooltipText ? tooltipText : ''}</Typography>
            </Grid>
          </CardContent>
        </Card>
      }
    >
      {chipComponent}
    </Tooltip>
  );
};

const Filter = ({
  listings,
  allListings,
  filterVal,
  setFilterVal,
  open,
  toggleDrawer,
  matches,
  setFilterApplied,
  resetFilter,
  setFilterActive,
  currency,
  revPotentialRangeInvestment,
  setIndividualRevenuePotentialRange,
  handleOnSampleClick,
  isListingLoading,
  priceTiers,
  isEstimatorAdvancedFilter,
}: FilterPropTypes) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [currentFilterTab, setCurrentFilteTab] = useState<
    'listing' | 'performance' | 'amenities' | 'others'
  >('listing');
  const upgradeMessage = useUpgradeMesageBasedOnFilterTab(currentFilterTab);

  const isAccessDenied = useCannotAccessFilter(currentFilterTab);

  // these local slider states ensure speed of slider updates
  const [accommodates, setAccommodates] = useState<number[]>([1, 16]);
  const [minimumNights, setMinimumNights] = useState<number[]>([1, 30]);
  const [rating, setRating] = useState<number[]>([3.9, 5]);

  //loads the previously saved filters from local storage
  useEffect(() => {
    //loads the saved room type from local storage
    const room_type_from_localStorage = localStorage.getItem('room_type');

    const _splited_room_type = (room_type_from_localStorage
      ? room_type_from_localStorage.split(',')
      : []) as string[];

    if (room_type_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, roomType: _splited_room_type };
      });
    }

    //loads the saved bedroom number from local storage
    const no_of_bedroom_from_localStorage = localStorage.getItem('bedroom_number');

    if (no_of_bedroom_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, bedrooms: no_of_bedroom_from_localStorage.split(',').map(Number) };
      });
    }

    //loads the saved bathroom number from local storage
    const no_of_bathroom_from_localStorage = localStorage.getItem('bathroom_number');

    if (no_of_bathroom_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, bathrooms: no_of_bathroom_from_localStorage.split(',').map(Number) };
      });
    }

    //loads the saved host type from local storage
    const host_type_from_localStorage = localStorage.getItem('host_type');

    if (host_type_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          hostType: host_type_from_localStorage as 'independent' | 'professional' | 'both',
        };
      });
    }

    //loads the saved minimum nights number from local storage
    const minimum_nights_from_localStorage = localStorage.getItem('minimum_nights');

    if (minimum_nights_from_localStorage) {
      setMinimumNights(minimum_nights_from_localStorage.split(',').map(Number));
    }

    //loads the number of reviews from the local storage
    const number_of_reviews_from_localStorage = localStorage.getItem('number_of_reviews');

    if (number_of_reviews_from_localStorage) {
      let review: number | 'all' =
        number_of_reviews_from_localStorage !== 'all'
          ? Number(number_of_reviews_from_localStorage)
          : 'all';
      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          number_of_reviews: review,
        };
      });
    }

    //loads the rating from localstorage
    const rating_from_localstorage = localStorage.getItem('rating');

    if (rating_from_localstorage) {
      const rating = rating_from_localstorage.split(',').map(Number);
      setRating(rating);
    }

    //loads the saved number of people can sleep from local storage
    const no_of_people_from_localStorage = localStorage.getItem('no_of_people');

    if (no_of_people_from_localStorage) {
      setAccommodates(no_of_people_from_localStorage.split(',').map(Number));
    }
  }, []);

  const [advancedFilterOpen, setAdvancedFilterOpen] = useState<boolean>(false);

  // Room Types
  const roomTypesObj = {
    entire_home: 'Entire House',
    condo: 'Apartment',
    private_room: 'Private Room',
    shared_room: 'Shared Room',
    hotel_room: 'Hotel Room',
  };

  // Immediately apply filter changes after setting filter values
  const applyFilterChanges = (filtersToConsider: string[] = []) => {
    if (isAccessDenied) {
      handleOnSampleClick();
      return;
    }
  };

  // Modify handleRoomTypeChange to apply filter immediately
  const handleRoomTypeChange = useCallback((value: string) => {
    setFilterVal((filterVal: FilterVal) => {
      let _room_type = filterVal.roomType;

      if (value !== 'all' && _room_type.includes('all')) {
        _room_type.splice(_room_type.indexOf('all'), 1);
      }

      if (_room_type.includes(value)) {
        _room_type = _room_type.filter(data => {
          return data !== value;
        });
      } else {
        _room_type.push(value);
      }

      if (value === 'all') {
        _room_type = ['all'];
      }

      let new_bedroom_val = filterVal.bedrooms;
      let new_bathroom_val = filterVal.bathrooms;

      if (
        !_room_type.includes('entire_home') &&
        !_room_type.includes('condo') &&
        !_room_type.includes('all')
      ) {
        new_bedroom_val = [100];
        new_bathroom_val = [100];
        localStorage.setItem('bedroom_number', new_bedroom_val.toString());
        localStorage.setItem('bathroom_number', new_bathroom_val.toString());
      }

      localStorage.setItem('room_type', _room_type.toString());

      return {
        ...filterVal,
        roomType: _room_type,
        bedrooms: new_bedroom_val as number[],
        bathrooms: new_bathroom_val,
      };
    });

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['roomType']), 0);
  }, []);

  // Modify handleBedroomsChange to apply filter immediately
  const handleBedroomsChange = (value: string | number) => {
    if (
      isNaN(Number(value)) &&
      !['private_room', 'hotel_room', 'shared_room'].includes((value as unknown) as string)
    ) {
      return null;
    }

    let newValue = filterVal.bedrooms;

    if (value !== 100 && newValue.includes(100)) {
      newValue.splice(newValue.indexOf(100), 1);
    }

    if (newValue.includes(value)) {
      newValue = newValue.filter(val => val !== value);
    } else {
      newValue.push(value);
    }

    if (value === 100) {
      newValue = [100];
    }

    const _room_type = filterVal.roomType;

    localStorage.setItem('bedroom_number', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, bedrooms: newValue as number[] };
    });

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['bedrooms']), 0);

    return null;
  };

  // Modify handleBathroomsChange to apply filter immediately
  const handleBathroomsChange = (value: string | number) => {
    value = Number(value);
    if (isNaN(value)) {
      return null;
    }

    let newValue = filterVal.bathrooms;

    if (value !== 100 && newValue.includes(100)) {
      newValue.splice(newValue.indexOf(100), 1);
    }
    if (newValue.includes(value)) {
      newValue = newValue.filter(val => val !== value);
    } else {
      newValue.push(value);
    }

    if (value === 100) {
      newValue = [100];
    }

    localStorage.setItem('bathroom_number', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, bathrooms: newValue as number[] };
    });

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['bathrooms']), 0);

    return null;
  };

  // Modify handleRevenueFilter to apply filter immediately
  const handleRevenueFilter = (value: number | 'all') => {
    if (isAccessDenied) return;

    if (value === 'all') {
      return setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, revenueFilter: ['all'] };
      });
    }

    if (isNaN(Number(value))) return null;

    let newValue: (number | 'all')[] = Array.isArray(filterVal.revenueFilter)
      ? [...filterVal.revenueFilter]
      : [];
    if (newValue.includes('all')) {
      newValue = newValue.filter(v => v !== 'all');
    }

    const toggledValue = Number(value);
    if (newValue.includes(toggledValue)) {
      newValue = newValue.filter(v => v !== toggledValue);
    } else {
      newValue.push(toggledValue);
    }

    // Map 4 tier indices (0-3) to 4 boundary keys (revenue amounts) for applyRevenueFilter
    const boundaries = getRevenueTierBoundaries(revPotentialRangeInvestment);
    const tempRevPotentialRange: { [key: number]: 'visible' | 'hidden' } = {};
    if (boundaries.length >= 4) {
      tempRevPotentialRange[boundaries[0]] = newValue.includes(0) ? 'visible' : 'hidden';
      tempRevPotentialRange[boundaries[1]] = newValue.includes(1) ? 'visible' : 'hidden';
      tempRevPotentialRange[boundaries[2]] = newValue.includes(2) ? 'visible' : 'hidden';
      tempRevPotentialRange[boundaries[3]] = newValue.includes(3) ? 'visible' : 'hidden';
      dispatch(setIndividualRevenuePotentialRange(tempRevPotentialRange));
    }

    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, revenueFilter: newValue };
    });

    setTimeout(() => applyFilterChanges(['revenueFilter']), 0);
  };

  // Determine if any price tier is selected
  const isAnyPriceTierToggled = Object.values(priceTiers).some(tier => tier.toggled);

  // Modify handlePriceTierFilter to apply filter immediately
  const handlePriceTierFilter = (value: string) => {
    let newValue: (number[] | 'all')[] = [];

    if (value === 'all') {
      newValue.push('all');
      setFilterVal((filterVal: FilterVal) => ({
        ...filterVal,
        priceTierFilter: newValue,
      }));

      // Unselect all other tiers
      const updatedPriceTiersState = Object.fromEntries(
        Object.entries(priceTiers).map(([tier, tierData]) => [
          tier,
          { ...tierData, toggled: false },
        ])
      ) as PriceTiers;
      dispatch(updatePriceTiers(updatedPriceTiersState));
      return null;
    }

    // Toggle the selected tier
    const updatedPriceTiersState = {
      ...priceTiers,
      [value]: {
        ...priceTiers[value],
        toggled: !priceTiers[value].toggled,
      },
    };

    // Update the filter array
    Object.keys(updatedPriceTiersState).forEach(tier => {
      if (updatedPriceTiersState[tier].toggled) {
        newValue.push(updatedPriceTiersState[tier].range);
      }
    });

    // If no other tier is selected, select 'Any'
    if (newValue.length === 0) {
      newValue.push('all');
    } else {
      // Unselect 'Any' if any other tier is selected
      const index = newValue.indexOf('all');
      if (index !== -1) {
        newValue.splice(index, 1);
      }
    }

    dispatch(updatePriceTiers(updatedPriceTiersState));
    setFilterVal((filterVal: FilterVal) => ({
      ...filterVal,
      priceTierFilter: newValue,
    }));

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['priceTierFilter']), 0);

    return null;
  };

  // Modify handleHostTypeChange to apply filter immediately
  const handleHostTypeChange = (value: 'independent' | 'professional' | 'both') => {
    localStorage.setItem('host_type', value);
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, hostType: value };
    });

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['hostType']), 0);
  };

  // Modify handleMinimumNightsChange to apply filter immediately
  const handleMinimumNightsChange = (event: Event, newValue: number | number[]) => {
    setMinimumNights(newValue as number[]);
    localStorage.setItem('minimum_nights', (newValue as number[]).toString());

    // Immediately apply the filter
    setTimeout(() => {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, minimum_nights: newValue as number[] };
      });
      applyFilterChanges(['minimum_nights']);
    }, 0);
  };

  // Minimum Nights Marks
  const minimumNightsMarks = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 30,
      label: '30+',
    },
  ];

  // Modify handleNumberOfReviewsChange to apply filter immediately
  const handleNumberOfReviewsChange = (value: number | 'all') => {
    localStorage.setItem('number_of_reviews', value.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, number_of_reviews: value };
    });

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['number_of_reviews']), 0);
  };

  // Modify handleRatingChange to apply filter immediately
  const handleRatingChange = (event: Event, newValue: number | number[]) => {
    setRating(newValue as number[]);
    localStorage.setItem('rating', (newValue as number[]).toString());

    // Immediately apply the filter
    setTimeout(() => {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, rating: newValue as number[] };
      });
      applyFilterChanges(['rating']);
    }, 0);
  };

  // Rating Marks
  const rating_marks = [
    {
      value: 3.9,
      label: '<4.0',
    },
    {
      value: 5.0,
      label: '5.0',
    },
  ];

  // Modify handleAccomodatesChange to apply filter immediately
  const handleAccomodatesChange = (event: Event, newValue: number | number[]) => {
    setAccommodates(newValue as number[]);
    localStorage.setItem('no_of_people', (newValue as number[]).toString());

    // Immediately apply the filter
    setTimeout(() => {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, accommodates: newValue as number[] };
      });
      applyFilterChanges(['accommodates']);
    }, 0);
  };

  // Accomodates Marks
  const accomodatesMarks = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 16,
      label: '16+',
    },
  ];

  // Modify handleAmenitiesChange to apply filter immediately
  const handleAmenitiesChange = (event, amenity) => {
    const checkboxType = getAmenityState(
      event.target.value as 'with' | 'without',
      event.target.checked
    );

    setFilterVal(prevFilterVal => {
      const newAmenitiesObject = { ...prevFilterVal.amenities };
      newAmenitiesObject[amenity] = checkboxType;

      return {
        ...prevFilterVal,
        amenities: newAmenitiesObject,
      };
    });

    // Immediately apply the filter
    setTimeout(() => applyFilterChanges(['amenities']), 0);
  };

  // Modify handleShowAllBookingcomListingsChange to apply filter immediately
  const handleShowAllBookingcomListingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowAllBookingComListingsChecked((e: boolean) => {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, showAllBookingComListings: event.target.checked };
      });

      // Immediately apply the filter
      setTimeout(() => applyFilterChanges(['showAllBookingComListings']), 0);

      return event.target.checked;
    });
  };

  const getCurrencySymbolForFilter = (index, limit) => {
    const selectedCurrency: string =
      exchangeRates[currency].symbol.length < 2 ? exchangeRates[currency].symbol : '$';
    return (
      <>
        {selectedCurrency.repeat(index)}
        <span style={{ color: '#ccc' }}>{selectedCurrency.repeat(limit - index)}</span>
      </>
    );
  };

  // Amenities
  const getAmenityState = (checkboxType: 'with' | 'without', isChecked: boolean) => {
    if (checkboxType === 'with') {
      return isChecked ? true : null;
    } else {
      return isChecked ? false : null;
    }
  };

  const [showAllBookingComListingsChecked, setShowAllBookingComListingsChecked] = useState(
    filterVal.showAllBookingComListings
  );

  const FilterTabs = [
    {
      label: 'Listing',
      value: 'listing',
      icon: null,
      onclick: () => {
        setCurrentFilteTab('listing');
      },
    },
    {
      label: 'Performance',
      value: 'performance',
      icon: null,
      onclick: () => {
        setCurrentFilteTab('performance');
      },
    },
    {
      label: 'Amenities',
      value: 'amenities',
      icon: null,
      onclick: () => {
        setCurrentFilteTab('amenities');
      },
    },
    {
      label: 'Others',
      value: 'others',
      icon: null,
      onclick: () => {
        setCurrentFilteTab('others');
      },
    },
  ];

  const FilterHeader = (
    <Grid container>
      <Grid item xs={8}>
        <Typography
          style={{
            fontSize: '20px',
            fontWeight: 500,
            color: '#000',
          }}
        >
          Filters
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Tooltip title="Close Filter">
          <CloseIcon sx={{ cursor: 'pointer', float: 'right' }} onClick={() => toggleDrawer()} />
        </Tooltip>
      </Grid>
    </Grid>
  );

  const isNotEntireHouseOrCondo: boolean =
    !filterVal.roomType.includes('entire_home') &&
    !filterVal.roomType.includes('condo') &&
    !filterVal.roomType.includes('all');

  const isNoListingAvailable =
    isNotEntireHouseOrCondo &&
    (!filterVal.bedrooms.includes(100) ||
      filterVal.bedrooms.length > 1 ||
      !filterVal.bathrooms.includes(100) ||
      filterVal.bathrooms.length > 1);

  // Custom Alert Component
  const CustomAlert = ({
    type,
    color,
    body,
    pushLeft,
  }: {
    type: 'info' | 'warning' | 'error';
    color: string;
    body: JSX.Element | string;
    pushLeft: boolean;
  }) => {
    const style = {
      width: matches ? 300 : 500,
    };

    if (pushLeft) {
      style['marginLeft'] = 0;
      style['paddingLeft'] = 0;
    }

    return (
      <Grid
        container
        item
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        className={classes.filterContainer}
        style={style}
      >
        <Alert severity={type}>
          <Typography variant="body2">{body}</Typography>
        </Alert>
      </Grid>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer}
      variant="temporary"
      sx={{
        height: 'auto',
        background: 'transparent',
        zIndex: matches ? FILTER_FLOAT_BUTTON_Z_INDEX_MOBILE : FILTER_FLOAT_BUTTON_Z_INDEX_DESKTOP,
        [`& .MuiDrawer-paper`]: { width: matches ? '100%' : FILTER_WIDTH },
      }}
    >
      <Grid container direction="column" id="filter">
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          className={classes.filterContainer}
          style={{ ...(matches ? { marginTop: '100px' } : {}) }}
        >
          <Grid
            style={{
              position: 'fixed',
              top: matches ? '0px' : '75px',
              background: 'white',
              height: 'auto',
              zIndex: theme.zIndex.drawer + 1,
              paddingTop: '20px',
              width: matches ? '90%' : '440px',
            }}
            item
            xs={12}
          >
            {FilterHeader}
            <TabBar
              tabList={FilterTabs}
              selectedCurrentTab={currentFilterTab}
              color={COLOR_PRIMARY_2}
              renderLabelCondition={() => {
                return true;
              }}
              actionButton={<></>}
              showActionButton={true}
            />
          </Grid>

          {isAccessDenied && (
            <Typography
              variant="body1"
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: COLOR_PRIMARY_2,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => {
                handleOnSampleClick('expert');
              }}
            >
              {upgradeMessage}
            </Typography>
          )}

          {currentFilterTab === 'listing' && (
            <>
              {!isEstimatorAdvancedFilter && (
                <Grid
                  container
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  style={{ width: '100%' }}
                >
                  <Grid item>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Typography
                        color="textSecondary"
                        style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                      >
                        Bedrooms
                      </Typography>
                      {isNotEntireHouseOrCondo && (
                        <CustomAlert
                          type="warning"
                          color={COLOR_PRIMARY_2}
                          body={
                            <div>
                              Bedroom filter can only be applied to "Entire House", "Apartment
                              (Condo)" and "Show all listings".
                            </div>
                          }
                          pushLeft={true}
                        />
                      )}
                      <Grid
                        xs={12}
                        item
                        container
                        justifyContent="flex-start"
                        style={{ width: '100%', gap: '5px' }}
                      >
                        <FilterChip
                          value={100}
                          label={'Any'}
                          handleOnClick={() => handleBedroomsChange(100)}
                          variant={filterVal.bedrooms.includes(100) ? 'filled' : 'outlined'}
                        />
                        {[0, 1, 2, 3, 4, 5, 6, 'private_room', 'shared_room', 'hotel_room'].map(
                          (bedRoomType, index) => (
                            <FilterChip
                              value={bedRoomType}
                              label={
                                typeof bedRoomType === typeof ''
                                  ? bedRoomType[0].toUpperCase()
                                  : bedRoomType === 0
                                  ? 'Studio'
                                  : bedRoomType === 6
                                  ? '6+'
                                  : bedRoomType
                              }
                              handleOnClick={() => handleBedroomsChange(bedRoomType)}
                              variant={
                                !filterVal.bedrooms.includes(bedRoomType) ||
                                filterVal.bedrooms.includes(100)
                                  ? 'outlined'
                                  : 'filled'
                              }
                              tooltipText={
                                roomTypesObj[bedRoomType] ? roomTypesObj[bedRoomType] : undefined
                              }
                            />
                          )
                        )}
                      </Grid>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {!isEstimatorAdvancedFilter && (
                <Grid
                  container
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  style={{ width: '100%' }}
                >
                  <Grid item>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Typography
                        color="textSecondary"
                        style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                      >
                        Bathrooms
                      </Typography>
                      {isNotEntireHouseOrCondo && (
                        <CustomAlert
                          type="warning"
                          color={COLOR_PRIMARY_2}
                          body={
                            <div>
                              Bathroom filter can only be applied to "Entire House", "Apartment
                              (Condo)" and "Show all listings".
                            </div>
                          }
                          pushLeft={true}
                        />
                      )}
                      <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                        <FilterChip
                          value={100}
                          label={'Any'}
                          handleOnClick={() => handleBathroomsChange(100)}
                          variant={filterVal.bathrooms.includes(100) ? 'filled' : 'outlined'}
                        />
                        {[0, 1, 2, 3, 4, 5, 6].map((bathRoomType, index) => (
                          <FilterChip
                            value={bathRoomType}
                            label={
                              bathRoomType === 6 ? '6+' : bathRoomType === 0 ? 'S' : bathRoomType
                            }
                            handleOnClick={() => handleBathroomsChange(bathRoomType)}
                            variant={
                              !filterVal.bathrooms.includes(bathRoomType) ||
                              filterVal.bathrooms.includes(100)
                                ? 'outlined'
                                : 'filled'
                            }
                            tooltipText={bathRoomType === 0 ? 'Shared Bathroom' : undefined}
                          />
                        ))}
                      </Grid>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                style={{ width: '100%' }}
              >
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                >
                  Sleeps
                </Typography>
                <Slider
                  size="small"
                  value={accommodates}
                  min={1}
                  max={16}
                  step={1}
                  marks={accomodatesMarks}
                  onChange={handleAccomodatesChange}
                  valueLabelDisplay="auto"
                  sx={{
                    color: COLOR_PRIMARY,
                  }}
                />
              </Grid>
            </>
          )}

          {currentFilterTab === 'performance' && (
            <>
              {/* review filter */}
              {Object.keys(priceTiers).length > 0 && (
                <Grid
                  container
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  style={{ width: '100%' }}
                >
                  <Grid item>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Typography
                        color="textSecondary"
                        style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                      >
                        Price Tiers
                      </Typography>
                      <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                        <FilterChip
                          value={'all'}
                          label={
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="body1">Any</Typography>
                            </div>
                          }
                          handleOnClick={() => handlePriceTierFilter('all')}
                          variant={!isAnyPriceTierToggled ? 'filled' : 'outlined'}
                          styles={{
                            width: '120px',
                            height: '120px',
                          }}
                        />
                        {Object.keys(priceTiers).map((tier, index) => {
                          const adrRangeLabel = getPriceTierRangeLabel(
                            tier,
                            priceTiers,
                            currency
                          );
                          return (
                            <FilterChip
                              key={`price-tier-${tier}`}
                              value={tier}
                              label={
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Typography variant="body1">
                                    {getCurrencySymbolForFilter(index + 1, 5)}
                                  </Typography>
                                  <Typography variant="body1">{tier}</Typography>
                                  <Typography
                                    variant="caption"
                                    style={{
                                      fontSize: '0.7rem',
                                      opacity: 0.75,
                                      marginTop: 2,
                                    }}
                                  >
                                    {adrRangeLabel}
                                  </Typography>
                                </div>
                              }
                              handleOnClick={() => handlePriceTierFilter(tier)}
                              variant={priceTiers[tier].toggled ? 'filled' : 'outlined'}
                              styles={{
                                width: '120px',
                                height: '120px',
                              }}
                            />
                          );
                        })}
                      </Grid>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {(() => {
                const boundaries = getRevenueTierBoundaries(revPotentialRangeInvestment);
                const isArray = Array.isArray(revPotentialRangeInvestment);
                const values = [0, 1, 2, 3]; // 4 tier indices
                const hasEnoughData = boundaries.length >= 3;
                return hasEnoughData ? (
                  <Grid
                    container
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    style={{ width: '100%' }}
                  >
                    <Grid item>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Typography
                          color="textSecondary"
                          style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                        >
                          Revenue Filter
                        </Typography>
                        <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                          <FilterChip
                            value={'all'}
                            label={
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant="body1">Any</Typography>
                              </div>
                            }
                            handleOnClick={() => handleRevenueFilter('all')}
                            variant={filterVal.revenueFilter.includes('all') ? 'filled' : 'outlined'}
                            styles={{
                              width: '120px',
                              height: '120px',
                            }}
                          />
                          {values.slice(0, 4).map((revPotential, index) => {
                            const tierName = REVENUE_TIER_NAMES[index];
                            const rangeLabel = getRevenueTierRangeLabel(index, boundaries, currency);
                            const isVisible =
                              !isArray && revPotentialRangeInvestment[revPotential] === 'visible';
                            return (
                              <FilterChip
                                key={`filter-chip-rev-${revPotential}`}
                                value={revPotential}
                                label={
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      style={{
                                        filter: isAccessDenied ? 'blur(6px)' : 'blur(0)',
                                        fontSize: '0.75rem',
                                        color: '#999',
                                        letterSpacing: '0.08em',
                                      }}
                                    >
                                      REV
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      style={{
                                        filter: isAccessDenied ? 'blur(6px)' : 'blur(0)',
                                      }}
                                    >
                                      {tierName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      style={{
                                        filter: isAccessDenied ? 'blur(6px)' : 'blur(0)',
                                        fontSize: '0.7rem',
                                        opacity: 0.75,
                                        marginTop: 2,
                                      }}
                                    >
                                      {rangeLabel}
                                    </Typography>
                                  </div>
                                }
                                handleOnClick={() => handleRevenueFilter(revPotential)}
                                variant={
                                  isArray
                                    ? filterVal.revenueFilter.includes(revPotential) &&
                                      !filterVal.revenueFilter.includes('all')
                                      ? 'filled'
                                      : 'outlined'
                                    : isVisible && !filterVal.revenueFilter.includes('all')
                                      ? 'filled'
                                      : 'outlined'
                                }
                                styles={{
                                  width: '120px',
                                  height: '120px',
                                }}
                              />
                            );
                          })}
                        </Grid>
                      </FormControl>
                    </Grid>
                  </Grid>
                ) : null;
              })()}

              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                style={{ width: '100%' }}
              >
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                >
                  Professionally Managed
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    defaultValue="both"
                    value={filterVal.hostType}
                    onChange={e => {
                      handleHostTypeChange(
                        e.target.value as 'independent' | 'professional' | 'both'
                      );
                    }}
                  >
                    <FormControlLabel
                      value="professional"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="independent"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="No"
                    />
                    <FormControlLabel
                      value="both"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="Both"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                style={{ width: '100%' }}
              >
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                >
                  Rating
                </Typography>

                <Slider
                  size="small"
                  value={rating}
                  min={3.9}
                  max={5}
                  step={0.1}
                  marks={rating_marks}
                  onChange={handleRatingChange}
                  valueLabelDisplay="auto"
                  valueLabelFormat={x => (x === 3.9 ? '<4.0' : x)}
                  sx={{
                    color: COLOR_PRIMARY,
                  }}
                />
              </Grid>

              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                style={{ width: '100%' }}
              >
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                >
                  Number of Reviews
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    defaultValue={filterVal.number_of_reviews}
                    value={filterVal.number_of_reviews}
                    onChange={e => {
                      handleNumberOfReviewsChange(e.target.value as number | 'all');
                    }}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="Any"
                    />
                    <FormControlLabel
                      value={1}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="1+"
                    />
                    <FormControlLabel
                      value={3}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="3+"
                    />
                    <FormControlLabel
                      value={10}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="10+"
                    />
                    <FormControlLabel
                      value={30}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="30+"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>
          )}

          {currentFilterTab === 'amenities' && (
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={3}
            >
              {/* categories */}
              {!isEstimatorAdvancedFilter && (
                <Grid item>
                  <FormControl style={{ width: '100%' }}>
                    <Typography
                      color="textSecondary"
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#000',
                        marginBottom: '10px',
                      }}
                    >
                      Categories
                      <Chip
                        label={'New'}
                        variant={'outlined'}
                        sx={{
                          minWidth: 50,
                          marginLeft: '10px',
                          background: 'linear-gradient(273.52deg, #7158E2 29.31%, #FF2079 97.77%)',
                          color: 'white',
                          fontWeight: 500,
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            width: 'calc(100% - 5px)',
                            height: 'calc(100% - 4px)',
                            borderRadius: '999px',
                            backgroundColor: 'white',
                            position: 'absolute',
                            zIndex: 0,
                            margin: '10px',
                          },
                          '& .MuiChip-label': {
                            zIndex: 2,
                            color: 'black',
                            background:
                              '-webkit-linear-gradient(273.52deg, #7158E2 29.31%, #FF2079 97.77%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          },
                        }}
                      />
                    </Typography>

                    <Grid
                      container
                      item
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{ width: '100px' }}
                    >
                      <Typography style={{ fontWeight: 500 }}>With</Typography>
                      <Typography style={{ fontWeight: 500, marginLeft: 2 }}>Without</Typography>
                    </Grid>

                    {[...Object.entries(categoriesObject)].map(([key, value]) => (
                      <Tooltip
                        title={
                          key == 'beachfront' &&
                          allListings.data &&
                          !isMarketBeachfront(allListings)
                            ? 'Market is not Beachfront'
                            : ''
                        }
                      >
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-start"
                          item
                          xs={12}
                        >
                          <Grid
                            container
                            item
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            style={{ width: '100px' }}
                          >
                            <Checkbox
                              value="with"
                              checked={filterVal.amenities[key] == true}
                              onChange={e => {
                                handleAmenitiesChange(e, key);
                              }}
                              disabled={
                                key == 'beachfront' &&
                                allListings.data &&
                                !isMarketBeachfront(allListings)
                              }
                            />
                            <Checkbox
                              value="without"
                              checked={filterVal.amenities[key] == false}
                              onChange={e => {
                                handleAmenitiesChange(e, key);
                              }}
                              disabled={
                                key == 'beachfront' &&
                                allListings.data &&
                                !isMarketBeachfront(allListings)
                              }
                            />
                          </Grid>

                          <Typography
                            style={{
                              marginLeft: '10px',
                              color:
                                key == 'beachfront' &&
                                allListings.data &&
                                !isMarketBeachfront(allListings)
                                  ? COLOR_GREY_400
                                  : COLOR_PRIMARY_1,
                              fontWeight: 500,
                              alignItems: 'center',
                              display: 'flex',
                              gap: '10px',
                            }}
                          >
                            {AmenitiesIcons[key]} {value}
                          </Typography>
                        </Grid>
                      </Tooltip>
                    ))}
                  </FormControl>
                </Grid>
              )}
              {/* most commmon amenities */}
              <Grid item>
                <FormControl style={{ width: '100%' }}>
                  <Typography
                    color="textSecondary"
                    style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#000',
                      marginBottom: '10px',
                    }}
                  >
                    Most Common
                  </Typography>

                  <Grid
                    container
                    item
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ width: '100px' }}
                  >
                    <Typography style={{ fontWeight: 500 }}>With</Typography>
                    <Typography style={{ fontWeight: 500, marginLeft: 2 }}>Without</Typography>
                  </Grid>

                  {[...Object.entries(commonAmenitiesObject)].map(([key, value]) => (
                    <Tooltip
                      title={
                        key == 'beachfront' && allListings.data && !isMarketBeachfront(allListings)
                          ? 'Market is not Beachfront'
                          : ''
                      }
                    >
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        item
                        xs={12}
                      >
                        <Grid
                          container
                          item
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{ width: '100px' }}
                        >
                          <Checkbox
                            value="with"
                            checked={filterVal.amenities[key] == true}
                            onChange={e => {
                              handleAmenitiesChange(e, key);
                            }}
                            disabled={
                              key == 'beachfront' &&
                              allListings.data &&
                              !isMarketBeachfront(allListings)
                            }
                          />
                          <Checkbox
                            value="without"
                            checked={filterVal.amenities[key] == false}
                            onChange={e => {
                              handleAmenitiesChange(e, key);
                            }}
                            disabled={
                              key == 'beachfront' &&
                              allListings.data &&
                              !isMarketBeachfront(allListings)
                            }
                          />
                        </Grid>

                        <Typography
                          style={{
                            marginLeft: '10px',
                            color:
                              key == 'beachfront' &&
                              allListings.data &&
                              !isMarketBeachfront(allListings)
                                ? COLOR_GREY_400
                                : COLOR_PRIMARY_1,
                            fontWeight: 500,
                            alignItems: 'center',
                            display: 'flex',
                            gap: '10px',
                          }}
                        >
                          {AmenitiesIcons[key]} {value}
                        </Typography>
                      </Grid>
                    </Tooltip>
                  ))}
                </FormControl>
              </Grid>
              {/* additional amenities*/}
              <Grid item>
                <FormControl style={{ width: '100%' }}>
                  <Typography
                    color="textSecondary"
                    style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#000',
                      marginBottom: '10px',
                    }}
                  >
                    Additional
                  </Typography>

                  <Grid
                    container
                    item
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ width: '100px' }}
                  >
                    <Typography style={{ fontWeight: 500 }}>With</Typography>
                    <Typography style={{ fontWeight: 500, marginLeft: 2 }}>Without</Typography>
                  </Grid>

                  {[...Object.entries(amenitiesObject)].map(([key, value]) => (
                    <Tooltip
                      title={
                        key == 'beachfront' && allListings.data && !isMarketBeachfront(allListings)
                          ? 'Market is not Beachfront'
                          : ''
                      }
                    >
                      <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        item
                        xs={12}
                      >
                        <Grid
                          container
                          item
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{ width: '100px' }}
                        >
                          <Checkbox
                            value="with"
                            checked={filterVal.amenities[key] == true}
                            onChange={e => {
                              handleAmenitiesChange(e, key);
                            }}
                            disabled={
                              key == 'beachfront' &&
                              allListings.data &&
                              !isMarketBeachfront(allListings)
                            }
                          />
                          <Checkbox
                            value="without"
                            checked={filterVal.amenities[key] == false}
                            onChange={e => {
                              handleAmenitiesChange(e, key);
                            }}
                            disabled={
                              key == 'beachfront' &&
                              allListings.data &&
                              !isMarketBeachfront(allListings)
                            }
                          />
                        </Grid>

                        <Typography
                          style={{
                            marginLeft: '10px',
                            color:
                              key == 'beachfront' &&
                              allListings.data &&
                              !isMarketBeachfront(allListings)
                                ? COLOR_GREY_400
                                : COLOR_PRIMARY_1,
                            fontWeight: 500,
                            alignItems: 'center',
                            display: 'flex',
                            gap: '10px',
                          }}
                        >
                          {AmenitiesIcons[key]} {value}
                        </Typography>
                      </Grid>
                    </Tooltip>
                  ))}
                </FormControl>
              </Grid>
            </Grid>
          )}

          {currentFilterTab === 'others' && (
            <>
              <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                <Grid item>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Typography
                      color="textSecondary"
                      style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                    >
                      Property Type
                    </Typography>
                    <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                      <FilterChip
                        value={'all'}
                        label={'Any'}
                        handleOnClick={() => handleRoomTypeChange('all')}
                        variant={filterVal.roomType.includes('all') ? 'filled' : 'outlined'}
                      />
                      {['entire_home', 'condo'].map((roomType, index) => (
                        <FilterChip
                          value={roomType}
                          label={roomTypesObj[roomType]}
                          handleOnClick={() => handleRoomTypeChange(roomType)}
                          variant={
                            !filterVal.roomType.includes(roomType) ||
                            filterVal.roomType.includes('all')
                              ? 'outlined'
                              : 'filled'
                          }
                        />
                      ))}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                style={{ width: '100%' }}
              >
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                >
                  Minimum Stay
                </Typography>
                <Slider
                  size="small"
                  value={minimumNights}
                  min={1}
                  max={30}
                  marks={minimumNightsMarks}
                  onChange={handleMinimumNightsChange}
                  valueLabelDisplay="auto"
                  sx={{
                    color: COLOR_PRIMARY,
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{
          position: 'fixed',
          bottom: 0,
          background: 'white',
          height: 'auto',
          zIndex: theme.zIndex.drawer + 1,
          padding: '20px 30px',
          width: matches ? '100%' : FILTER_WIDTH,
        }}
        item
        xs={12}
      >
        <Button
          size="large"
          variant="outlined"
          sx={{ color: COLOR_PRIMARY_2 }}
          onClick={() => {
            if (!isAccessDenied) {
              resetFilter();
              setRating([3.9, 5]);
              setAccommodates([1, 16]);
              setMinimumNights([1, 30]);

              // Apply reset immediately
              setTimeout(() => applyFilterChanges(), 0);
            }
          }}
        >
          Reset
        </Button>

        <Button
          id="filterUpdateBtn"
          size="large"
          variant="outlined"
          onClick={() => toggleDrawer()} // Just close the drawer since filters are applied immediately
          sx={{
            color: 'white',
            background: COLOR_PRIMARY_2,
            ':hover': { color: 'white', background: COLOR_PRIMARY_2 },
          }}
        >
          {isNoListingAvailable ? 'No Listing Available' : !isListingLoading ? 'Done' : 'Loading'}
          {isListingLoading && <CircularProgress style={{ color: '#fff', marginLeft: '10px' }} />}
        </Button>
      </Grid>
    </Drawer>
  );
};

export default Filter;

// Filter Local Storage Map:
//---------------------------------------------------------
// Variable                             Local Storage
//---------------------------------------------------------
// Room Type					            ==		room_type
// bedroom number           			==		bedroom_number
// bathrooms number        			  ==		bathroom_number
// minimum nights             		==		minimum_nights
// Accomodates (no of people)		  ==		no_of_people
// Host Type					            ==		host_type
// Amenities					            ==		amenities { all: boolean; hot_tub: boolean; pool: boolean; }
//---------------------------------------------------------
